/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import { ComponentManagerFn } from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { toOptions } from '../../../utils/ManagerUtils';
import { defaultValue } from './ColumnFooter';

const ColumnFooterManager: ComponentManagerFn<'ColumnFooter'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ColumnFooter'>(platform, 'ColumnFooter'),
    type: 'ColumnFooter',
    name: 'Column Footer',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      summaryFunction: {
        label: 'Summary Function',
        type: 'select',
        category: 'UI',
        options: async (item, { appUid, pageId }) => {
          const parent = platform.getItem(appUid, pageId, item.parent);
          return toOptions(
            parent?.type === 'NumberColumn'
              ? ['sum', 'avg', 'min', 'max', 'count', 'none']
              : ['count', 'none'],
          );
        },
        useDropDown: (item, { appUid, pageId }) => {
          const parent = platform.getItem(appUid, pageId, item.parent);
          return parent?.type === 'NumberColumn';
        },
        required: true,
      },
      formatExpression: {
        label: 'Format Expression',
        type: 'expressionOnly',
        category: 'UI',
        returnType: () => 'string',
        helpText:
          // eslint-disable-next-line no-template-curly-in-string
          "Use $._.summary to access the calculated summary value. e.g. `${t('Sum')}: ${formatNumber($._.summary)}`",
        placeholder: 'formatNumber($._.summary)',
      },
      excelSummaryFunction: {
        label: 'Excel Summary Expression',
        type: 'string',
        category: 'Export',
        returnType: () => 'string',
        helpText:
          'Use sum(#{attributeCode}) + min(#{attributeCode}) (Availabe functions: sum, avg, min, max, count)',
        placeholder: 'sum(#{attributeCode})',
      },
      excelSummaryFormatter: {
        label: 'Excel Summary Formatter',
        type: 'string',
        category: 'Export',
        returnType: () => 'string',
        helpText: 'Use ###.#### , $###,###.## , ##.00% etc',
      },
    },
    isAllowedInsideParent: (page, parent, { appUid, itemId, pageId, mgr }) => {
      // footer is not yet supported for canvas grid
      if (platform.findParentOfType(appUid, pageId, itemId, 'CanvasGrid')) {
        return `${mgr.name} is not supported inside Canvas Grid!`;
      }
      return true;
    },
    getDisplayText: (item) => item.props.summaryFunction,
    category: 'Forms',
    icon: { iconName: 'sigma' },
    description: `**Overview**<br/>
The Column Footer component is an aggregation tool attached to individual columns within a Data Grid (excluding Canvas Grids). Its primary purpose is to calculate and display summary statistics—such as totals, averages, or counts—based on the data currently loaded in that specific column.
Use this component to provide quick insights at the bottom of tabular data, enabling users to see metrics like "Total Revenue" or "Item Count" at a glance without needing to run separate reports.

**1. Aggregation Logic**<br/>
The footer automatically detects the type of column it is attached to and offers appropriate summary functions.
* **Summary Function:** Defines the mathematical operation applied to the column data.
* **Number Columns:** Supports **Sum**, **Average**, **Min**, **Max**, **Count**, and **None**.
* **Other Columns (String, Date, etc.):** Limited to **Count** or **None**.

**2. Display Formatting**<br/>
Once the summary value is calculated, you must define how it is rendered in the UI. This is controlled via TypeScript expressions.
* **Format Expression:** A string expression used to style the output.
* **Accessing the Value:** Use the special variable \`$._.summary\` to access the calculated result.
* **Example (Currency):** \`Total: \${formatCurrency($._.summary)}\`
* **Example (Rounding):** \`Avg: \${Math.round($._.summary)}\`

**3. Excel Export Configuration**<br/>
When a user exports the Grid data to Excel, the browser-based summary calculation does not automatically transfer. You must explicitly define the formula and formatting for the generated Excel file.
* **Excel Summary Expression:** The formula syntax for Excel. You can use the placeholder \`#{attributeCode}\` to reference the current column's data field (e.g., \`sum(#{attributeCode})\`).
* **Excel Summary Formatter:** Standard Excel formatting masks (e.g., \`$###,###.##\` for currency).

**4. Interactivity**<br/>
The Column Footer is not just a display element; it can also be interactive.
* **Trigger Actions:** The component allows **TriggerOnClick** as a child. This means you can configure the footer to execute logic when clicked.
* **Use Case:** Clicking a "Total Error Count" footer could open a modal showing a detailed log of those errors.

**5. Best Use Cases**<br/>
* **Financial Tables:** Displaying Grand Totals for "Amount" or "Tax" columns using the **sum** function.
* **Inventory Lists:** Showing the total number of items using **count**.
* **Performance Metrics:** Calculating the **avg** (average) processing time in a log table.
* **Export-Ready Reports:** Ensuring that when clients download a report, the footer rows in Excel contain live formulas rather than static text.`,
  };
};

export default ColumnFooterManager;
